import type {
  AiAction,
  AiState,
  AiStatusNotification,
  DerivedAiStatus,
  GetAiPredictionStatusForDateRangeBody,
  QueryAiPredictionStatusForDateRange,
} from '@timelyapp/integration-cloud-api-spec';
import keyBy from 'lodash-es/keyBy';
import { DateTime } from 'luxon';

export type DeriveAiStatusParams = {
  ai_enabled: boolean;
  last_memory_updated_at: string | null;
  ai_supported?: boolean;
  /** _When set_, debug messages will be printed. Works with `utils/debug` */
  debugCallback?: (message: string, params: Record<string, any>) => void;
};

/**
 * Derives the AI status and next action based on the current state.
 *
 * Important behaviors:
 * - When model_reset > trainingCompleteAt, we treat trainingCompleteAt as null (untrained model)
 * - For multiple events of the same type, we use the most recent one
 *
 * @todo - Make it understand "unprocessed body.status[] events"
 *
 * @warning Relies in transformLogsToStatuses
 * @see ai_prediction_logs.transformers.ts#transformLogsToStatuses & transformRecords
 *
 */
export const deriveAiStatus = (
  query: Required<Pick<QueryAiPredictionStatusForDateRange, 'from_date' | 'to_date'>>,
  body: GetAiPredictionStatusForDateRangeBody,
  params: DeriveAiStatusParams
): DerivedAiStatus => {
  const { from_date, to_date } = query;
  const { status, dates_updated_at } = body;
  const { ai_supported = true } = params;

  // Determine if the function is intended for a single day
  const date = from_date === to_date ? from_date : undefined;
  if (!date) {
    params.debugCallback?.('Skipping, only works for day view', { from_date, to_date });
    return { currentState: null, nextAction: null, dateLastInferedAt: null };
  }

  const now = DateTime.now();
  const date_updated_at = dates_updated_at[date];
  const statusMap = keyBy(status, 'event');
  const activeDate = DateTime.fromISO(date);
  const isDateInFuture = activeDate > now;
  const isDateInPast = activeDate.startOf('day') < now.startOf('day');

  // Extract pending events
  const inferencePendingAt = statusMap.inference_pending?.at
    ? DateTime.fromISO(statusMap.inference_pending.at)
    : null;

  const trainingPendingAt = statusMap.training_pending?.at
    ? DateTime.fromISO(statusMap.training_pending.at)
    : null;

  const modelResetAt = statusMap.model_reset?.at
    ? DateTime.fromISO(statusMap.model_reset.at)
    : null;

  const trainingCompleteAt = statusMap.training_complete?.at
    ? DateTime.fromISO(statusMap.training_complete.at)
    : null;

  const inferenceCompleteAt = statusMap.inference_complete?.at
    ? DateTime.fromISO(statusMap.inference_complete.at)
    : null;
  const inferenceRequestedAt = statusMap.inference_requested?.at
    ? DateTime.fromISO(statusMap.inference_requested.at)
    : null;
  const inferenceFailedAt = statusMap.inference_failed?.at
    ? DateTime.fromISO(statusMap.inference_failed.at)
    : null;
  const lastMemoryAt = params.last_memory_updated_at;
  const lastMemoryEndedAt = lastMemoryAt ? DateTime.fromISO(lastMemoryAt) : null;
  const dateLastInferedAt = date_updated_at ? DateTime.fromISO(date_updated_at) : null;

  // If we have made inferences in the past for this range, we know model exists.
  // This helps us determine if interence can be run _even if model is pending/currently training_
  const modelExistsButMayBeOutdated =
    !!inferenceCompleteAt || Object.keys(dates_updated_at).length > 0;

  // Check if pending events are most recent
  // We're in a pending state when the pending timestamp is in the future
  // and there's no requested event yet, or the requested event came after the pending event was set
  const isInferencePending =
    inferencePendingAt && (!inferenceRequestedAt || inferencePendingAt > inferenceRequestedAt);

  const isTrainingPending =
    trainingPendingAt &&
    (!statusMap.training_requested?.at ||
      trainingPendingAt > DateTime.fromISO(statusMap.training_requested.at));

  const isTrainingRequested = !!statusMap.training_requested?.at;
  const isTrainingRunning = !!statusMap.training_running?.at;

  // Variables to store derived state and action
  let currentState: AiState = 'DISABLED';
  let nextAction: AiAction | null = null;

  // Helper function for debug information
  // This calls on debug.template.ai for consistent styling
  const debugInfo = (message: string, details: Record<string, any> = {}) => {
    if (!params.debugCallback) return;
    const debugDetails = {
      nextAction,
      currentState,
      date,
      isDateInFuture,
      isDateInPast,
      aiFeatureSupported: ai_supported,
      trainingCompleteAt: trainingCompleteAt?.toISO() ?? null,
      modelResetAt: modelResetAt?.toISO() ?? null,
      inferenceCompleteAt: inferenceCompleteAt?.toISO() ?? null,
      inferenceRequestedAt: inferenceRequestedAt?.toISO() ?? null,
      inferenceFailedAt: inferenceFailedAt?.toISO() ?? null,
      inferencePendingAt: inferencePendingAt?.toISO() ?? null,
      trainingPendingAt: trainingPendingAt?.toISO() ?? null,
      isInferencePending,
      isTrainingPending,
      lastMemoryEndedAt: lastMemoryEndedAt?.toISO() ?? null,
      dateLastInferedAt: dateLastInferedAt?.toISO() ?? null,
      statusMap,
      now: now.toISO(),
      ...details,
      subtopic: 'Trigger Decision',
    };
    // Assuming the a console.log-like function is available
    params.debugCallback(`${message}`, debugDetails);
  };

  params.debugCallback?.('Updated status to check...', { body, params, subtopic: 'Trigger Check' });

  // ❌ AI is not supported, nothing to do..
  if (!ai_supported) {
    debugInfo('❌ AI not supported, skipping trigger checks..');
    currentState = 'NOT_SUPPORTED';
  }
  // ❌ AI is disabled by user, nothing to do..
  else if (!params.ai_enabled) {
    debugInfo('❌ AI disabled by user, skipping trigger checks..');
    currentState = 'DISABLED';
  } else {
    // ✅ Model is trained + no pending model training
    if (trainingCompleteAt) {
      if (isInferencePending) {
        currentState = 'INFERENCE_PENDING';
        debugInfo('⏳ Inference is pending via debounce');
      } else if (inferenceRequestedAt) {
        currentState = 'INFERENCE_RUNNING';
        debugInfo('⌛️ Inference already running, idling...');
      } else if (inferenceFailedAt) {
        if (inferenceFailedAt < now.minus({ minutes: 5 })) {
          currentState = 'RETRY_INFERENCE';
          nextAction = 'RE_RUN_INFERENCE_ON_DATE';
          debugInfo('✅ Inference failed over 5mins ago, re-infering');
        } else {
          currentState = 'WAITING_AFTER_FAIL';
          debugInfo('⌛️ Inference failed less than 5mins ago, idling...');
        }
      } else if (!dateLastInferedAt) {
        if (lastMemoryEndedAt) {
          currentState = 'FIRST_INFERENCE_ON_DATE';
          nextAction = 'RUN_FIRST_INFERENCE_ON_DATE';
          debugInfo('✅ Running first inference on day, with memories');
        } else {
          currentState = 'WAITING_FOR_MEMORIES';
          debugInfo('⌛️ No memories on day, so we skip inference run');
        }
      } else if (dateLastInferedAt && dateLastInferedAt < trainingCompleteAt) {
        if (dateLastInferedAt.plus({ minutes: 5 }) < trainingCompleteAt) {
          currentState = 'INFERRED_ON_OLD_MODEL';
          nextAction = 'RE_RUN_INFERENCE_ON_DATE';
          debugInfo('✅ Inferences are at least 5min older than model, re-infering...');
        } else {
          currentState = 'WAITING_FOR_MODEL_TO_MATURE';
          debugInfo('⌛️ Inferences were made within 5mins of last model training, idling..');
        }
      } else if (lastMemoryEndedAt) {
        if (isDateInFuture) {
          currentState = 'SKIP_FUTURE_DATE';
          debugInfo('⌛️ Date is in future, ignoring Memories as trigger');
        } else if (lastMemoryEndedAt > now) {
          currentState = 'SKIP_UNSAFE_MEMORIES';
          debugInfo('⌛️ Memory ended in the future, ignoring, unsafe to use as trigger');
        } else if (
          dateLastInferedAt &&
          lastMemoryEndedAt > dateLastInferedAt.plus({ minutes: 10 })
        ) {
          currentState = 'NEW_MEMORIES_TO_INFER';
          nextAction = 'RE_RUN_INFERENCE_ON_DATE';
          debugInfo('✅ Memories made inference stale by 10mins, re-infering');
        } else if (isDateInPast) {
          currentState = 'NO_NEW_MEMORIES_ON_PAST_DATE';
          debugInfo('⌛️ No new memories since last inference, idling..');
        } else {
          currentState = 'WAITING_FOR_SETTLED_MEMORIES';
          debugInfo('⌛️ Inference made within 10m of last memory, waiting a bit..');
        }
      } else {
        currentState = 'INFERENCE_UP_TO_DATE';
        debugInfo('⌛️ Inference is up-to-date, no need to refresh');
      }
    }

    // ❌ Model is NOT trained (OR training is in progress/pending)
    else {
      // ℹ️ If we have a model, still ALLOW inferences to run if we have memories:
      // This (hopefully) gets us out of "stuck" state when model is pending
      const allowInferenceWhileTraining =
        lastMemoryEndedAt && // We have memories to infer
        modelExistsButMayBeOutdated && // Model exists (but may be outdated)
        !isInferencePending && // No inference pending
        !inferenceRequestedAt; // No inreference requested/running
      // ... so long as date is not inferred yet, or 10min older than last memory
      const executeInferWhileTraining =
        allowInferenceWhileTraining &&
        (!dateLastInferedAt || lastMemoryEndedAt > dateLastInferedAt.plus({ minutes: 10 }));

      if (isTrainingPending) {
        currentState = 'TRAINING_PENDING';
        if (executeInferWhileTraining) {
          debugInfo(`⏳ Training is pending, ✅ BUT inference is allowed to run simultaneously`);
          nextAction = dateLastInferedAt
            ? 'RE_RUN_INFERENCE_ON_DATE'
            : 'RUN_FIRST_INFERENCE_ON_DATE';
        } else {
          debugInfo('⏳ Training is pending, waiting to execute...');
        }
      } else if (isTrainingRequested) {
        currentState = 'TRAINING_REQUEST_IN_QUEUE';
        if (executeInferWhileTraining) {
          debugInfo(`⌛️ Training is queued, ✅ BUT inference is allowed to run simultaneously`);
          nextAction = dateLastInferedAt
            ? 'RE_RUN_INFERENCE_ON_DATE'
            : 'RUN_FIRST_INFERENCE_ON_DATE';
        } else {
          debugInfo('⌛️ Training queued, ❌ not allowed to run inference, idling...');
        }
      } else if (isTrainingRunning) {
        currentState = 'TRAINING_RUNNING';
        if (executeInferWhileTraining) {
          debugInfo(`⌛️ Training is running, ✅ BUT inference is allowed to run simultaneously`);
          nextAction = dateLastInferedAt
            ? 'RE_RUN_INFERENCE_ON_DATE'
            : 'RUN_FIRST_INFERENCE_ON_DATE';
        } else {
          debugInfo('⌛️ Training running, ❌ not allowed to run inference, idling...');
        }
      } else if (statusMap.training_uninitialized?.at) {
        currentState = 'MODEL_NOT_TRAINED';
        debugInfo(`✅ Model doesn't exist yet, waiting for time entry changes before training...`);
      } else if (statusMap.training_data_insufficient?.at) {
        currentState = 'INSUFFICIENT_TRAINING_DATA';
        debugInfo(`⌛️ Insufficient time entries to train model, idling...`);
      } else if (statusMap.training_failed?.at && !statusMap.inference_requested?.at) {
        const lastTrainingFailedAt = DateTime.fromISO(statusMap.training_failed.at);
        if (lastTrainingFailedAt < now.minus({ minutes: 10 })) {
          currentState = 'RETRY_MODEL_TRAINING';
          nextAction = 'RE_RUN_MODEL_TRAINING';
          debugInfo('✅ Model training failed over 10m ago, trying again..');
        } else {
          currentState = 'WAITING_AFTER_FAIL';
          debugInfo('⌛️ Model training failed less than 10m ago, waiting a bit..');
        }
      } else {
        currentState = 'MODEL_NOT_TRAINED';
        debugInfo('🤔 Model is not trained, nothing to do?');
      }
    }
  }
  // Generate notification based on the current state
  const notification = generateNotification(currentState);

  // Return the final derived state, action, and notification
  return {
    currentState,
    nextAction,
    notification,
    dateLastInferedAt: dateLastInferedAt ? dateLastInferedAt.toISO() : null,
  };
};

/**
 * Generates a user-friendly notification based on the AI state
 */
const generateNotification = (state: AiState | null): AiStatusNotification => {
  // Default notification (fallback)
  const defaultNotification: AiStatusNotification = {
    title: 'Timesheet Assistant',
    message: state || 'Unknown status',
    variant: 'info',
  };

  // Return early if state is null
  if (!state) return defaultNotification;

  switch (state) {
    // DISABLED STATES
    case 'DISABLED':
      return {
        title: 'Timesheet Assistant',
        message: 'Suggestions turned off',
        variant: 'neutral',
      };

    case 'NOT_SUPPORTED':
      return {
        title: 'Timesheet Assistant',
        message: 'Not available on your current subscription',
        variant: 'neutral',
      };

    // PROCESSING STATES
    case 'INFERENCE_PENDING':
      return {
        title: 'Timesheet Assistant',
        message: 'About to update your suggestions',
        variant: 'neutral',
      };

    case 'TRAINING_PENDING':
      return {
        title: 'Timesheet Assistant',
        message: 'About to learn from recent time tracking',
        variant: 'neutral',
      };

    case 'TRAINING_REQUEST_IN_QUEUE':
      return {
        title: 'Timesheet Assistant',
        message: 'In queue to learn from recent time tracking and update suggestions',
        variant: 'neutral',
      };

    case 'FIRST_INFERENCE_ON_DATE':
    case 'INFERENCE_RUNNING':
    case 'RETRY_INFERENCE':
      return {
        title: 'Timesheet Assistant',
        message: 'Your suggestions are being created',
        variant: 'info',
      };

    case 'NEW_MEMORIES_TO_INFER':
      return {
        title: 'Timesheet Assistant',
        message: 'Your suggestions are being updated',
        variant: 'info',
      };

    case 'WAITING_AFTER_FAIL':
      return {
        title: 'Timesheet Assistant',
        message: 'Had some trouble, will try again shortly',
        variant: 'neutral',
      };

    case 'WAITING_FOR_MEMORIES':
      return {
        title: 'Timesheet Assistant',
        message: 'Waiting on Memories to be uploaded',
        variant: 'neutral',
      };

    case 'SKIP_FUTURE_DATE':
      return {
        title: 'Timesheet Assistant',
        message: 'Your assistant is unavailable on future dates',
        variant: 'neutral',
      };

    case 'INFERRED_ON_OLD_MODEL':
      return {
        title: 'Timesheet Assistant',
        message: 'Your suggestions will update soon.',
        variant: 'neutral',
      };

    case 'WAITING_FOR_MODEL_TO_MATURE':
    case 'NO_NEW_MEMORIES_ON_PAST_DATE':
    case 'SKIP_UNSAFE_MEMORIES':
    case 'INFERENCE_UP_TO_DATE':
    case 'WAITING_FOR_SETTLED_MEMORIES':
      return {
        title: 'Timesheet Assistant',
        message: 'Your suggestions are up to date',
        variant: 'success',
      };

    case 'INITIAL_TRAINING':
    case 'RETRY_MODEL_TRAINING':
    case 'TRAINING_RUNNING':
      return {
        title: 'Timesheet Assistant',
        message: 'Learning from your recent time tracking, updated suggestions coming shortly',
        variant: 'info',
      };

    case 'INSUFFICIENT_TRAINING_DATA':
    case 'MODEL_NOT_TRAINED':
      return {
        title: 'Timesheet Assistant',
        message: 'Not ready yet. Log more time to train your assistant',
        variant: 'neutral',
      };

    default:
      return defaultNotification;
  }
};
